import WarningIcon from "@assets/icons/warning_32px.svg?react";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import {
  BaseProjectProps,
  ProjectArchivingState,
} from "@custom-types/project-types";
import { Dialog, neutral, yellow } from "@flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useToast } from "@hooks/use-toast";
import { Alert, Typography } from "@mui/material";
import { deleteProject } from "@store/projects/projects-slice-thunk";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface DeleteProjectProps extends BaseProjectProps {
  /** Callback when the dialog closes */
  onClose: () => void;
}

/** Renders the delete button and delete the project on click with confirmation dialog */
export function DeleteProject({
  project,
  onClose,
}: DeleteProjectProps): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);
  const { showToast } = useToast();
  const { navigateToProjects } = useAppNavigation();
  const { trackEvent } = useTrackEvent();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  async function deleteSelectedProject(): Promise<void> {
    if (companyId) {
      trackEvent({ name: ProjectEvents.deleteProject });

      setIsProcessing(true);
      try {
        // Using unwrap makes sure that showToast is only shown if there is no error
        await dispatch(
          deleteProject({
            companyId,
            projectId: project.id,
          })
        ).unwrap();

        showToast({
          type: "success",
          message: "Project deleted successfully",
        });

        navigateToProjects({
          companyId,
          projectStatus: ProjectArchivingState.archived,
        });

        // error is handled by the deleteProject thunk in errors-slice
      } finally {
        setIsProcessing(false);
      }
    }
  }

  return (
    <>
      {/* Dialog to confirm before delete */}
      <Dialog
        title="Delete Project"
        confirmText="Delete"
        open={true}
        onConfirm={deleteSelectedProject}
        onCancel={onClose}
        showSpinner={isProcessing}
        variant="danger"
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: neutral[800],
            mb: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          When deleting the{" "}
          <var>
            <b>{project.name}</b>
          </var>{" "}
          project, all data will be lost.
        </Typography>

        <Alert
          severity="warning"
          icon={<WarningIcon width="20px" height="20px" />}
          sx={{
            backgroundColor: yellow[50],
            alignItems: "center",
            py: "0px",
          }}
        >
          <Typography sx={{ fontSize: "14px", color: neutral[800], py: "7px" }}>
            Deleted projects can not be recovered.
          </Typography>
        </Alert>
      </Dialog>
    </>
  );
}
